import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from "react-router-dom";
import { Tooltip } from 'rizzui';
import { toast } from 'sonner';
import DeletePopover from '../../../components/buttons/DeletePopover';
import StatusDropdown from '../../../components/forms/StatusDropdown';
import { Loader } from '../../../components/loader/SpinnerLogo';
import TabNav, { MenuItem } from "../../../components/navigation/TabNav";
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import useKoReasons from '../../../hooks/api-calls/useKoReasons';
import useLeadStates from '../../../hooks/api-calls/leads/useLeadStates';
import useFetch from '../../../hooks/useFetch';
import useHandleErrors from '../../../hooks/useHandleErrors';
import PageHeader from "../../../layout/shared/page-header";
import { setSelectedView } from '../../../redux/leadProfileSlice';
import { RootState } from '../../../redux/store';
import { menuRoutes } from "../../../router/menu";
import { LeadsService } from '../../../services/leads/leadsService';
import { Lead } from '../../../type/entities/leads-type';

const menuItems: MenuItem[] = [
    {
        label: 'Información',
        path: '/info',
        permission: {
            group: 'leads',
            action: 'get_leads',
        },
    },
    {
        label: 'Histórico de Estados',
        path: '/status-history',
        permission: {
            group: 'leads',
            action: 'get_leads',
        },
    },
    {
        label: 'Comentarios',
        path: '/comments',
        permission: {
            group: 'leads',
            action: 'get_leads',
        },
    }
];

export default function LeadProfileLayout({ isLoading, children }: { isLoading?: boolean, children: React.ReactNode }) {

    const { selectedView } = useSelector((state: RootState) => state.leadProfile || { selectedView: '/info' });
    const { id = '' } = useParams<{ id: string }>();
    const { userCan } = usePrivilege();
    const navigate = useNavigate();
    const { handleErrors } = useHandleErrors();
    const { getLeadStatesList } = useLeadStates();
    const { koReasonsOptions } = useKoReasons();

    const pageHeader = {
        title: 'Información del Lead',
        breadcrumb: [
            { name: 'Ventas' },
            { name: 'Gestión de contratos' },
            { name: 'Renovaciones (Leads)', href: menuRoutes.leads.path },
            { name: 'Perfil del Lead' },
        ],
    };

    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await (new LeadsService()).getLead(id);
        return response.getResponseData() as Lead;
    }, [id]));

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES CAMBIAR ESTADO
     * @EN CHANGE STATUS
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleStateChange = async (id: string, stateId: string, comment: string) => {
        var response = { success: false };
        try {
            response = await (await (new LeadsService()).changeLeadStatus(id, stateId, comment)).getResponseData();
            if (response.success) {
                toast.success('Estado del lead actualizado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            toast.error('Error al actualizar el estado');
        }
        refetch();
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINAR LEAD
     * @EN DELETE LEAD
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        try {
            const response = await (await (new LeadsService()).deleteLead(id)).getResponseData();
            if (response.success) {
                navigate(menuRoutes.leads.path);
                toast.success('Lead eliminado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            toast.error('Error al eliminar el lead');
        }
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    useEffect(() => {
        setTimeout(() => {
            navigate(`${menuRoutes.leads.path}/${id}/profile${selectedView}`);
        }, 300);

    }, [selectedView]);

    return (
        <>
            <PageHeader title={pageHeader.title} breadcrumb={pageHeader.breadcrumb}>
                <div className='flex flex-row flex-nowrap gap-3 mt-3 md:mt-0'>
                    {data && userCan('change_expiration_statuses', 'expirations') && (
                        <StatusDropdown
                            badgeSize='sm'
                            entityId={data?.id}
                            title="¿Estás seguro de cambiar el estado del vencimiento?"
                            statesOptions={getLeadStatesList(data?.currentStatus?.id)}
                            currentState={{ id: data?.currentStatusId || data?.currentStatus?.id, name: data?.currentStatus?.name, color: data?.currentStatus?.color, date: data?.currentStatusDate?.date }}
                            currentStateDate={data?.currentStatusDate?.date}
                            handleStateChange={handleStateChange}
                            koReasonsOptions={koReasonsOptions}
                            koReasonStatusEntity="lead"
                        />
                    )}

                    {userCan('delete_leads', 'leads') && (
                        <Tooltip content={'Eliminar lead'} placement={'top'} color={'invert'} size={'sm'} className='text-danger'>
                            <DeletePopover
                                title={`Eliminar lead`}
                                description={`¿Estás seguro de que deseas eliminar el lead?`}
                                size={'md'}
                                onDelete={() => handleDelete(id)}
                                actionIconClassName='h-auto w-10'
                            />
                        </Tooltip>
                    )}
                </div>
            </PageHeader>

            <TabNav menuItems={menuItems} setSelectedView={setSelectedView} />

            {isLoading ? <Loader height='60vh' /> : children}
        </>
    );
}